import {HttpClient, HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BackendConfiguration} from '@application/configuration/backend-configuration';
import {DitaManualType} from '@domain/dita-manual-type.enum';
import {MachineGeneralDocumentation} from '@domain/machine/machine-general';
import {MachineManual} from '@domain/machine/machine-manual';
import {MachineSchematic} from '@domain/machine/machine-schematic';
import {MachineSparePartsCatalogue} from '@domain/machine/machine-spare-parts-catalogue';
import {MachineTechnicalWeaveSettings} from '@domain/machine/machine-technical-weave-settings';
import {MachineTechnicalWeaveSettingsRevision} from '@domain/machine/machine-technical-weave-settings-revision';
import {OverviewListMachineForDocumentation} from '@domain/machine/overview-list-machine-for-documentation';
import {map} from 'lodash-es';
import {Observable, of as observableOf} from 'rxjs';
import {map as rxjsMap} from 'rxjs/operators';
import {MachinesDocumentation} from './machines-documentation';

@Injectable()
export class HttpMachinesDocumentationService implements MachinesDocumentation {
  private httpClient: HttpClient;
  private backendConfiguration: BackendConfiguration;

  public constructor(httpClient: HttpClient, backendConfiguration: BackendConfiguration) {
    this.httpClient = httpClient;
    this.backendConfiguration = backendConfiguration;
  }

  public delete(id: number): Observable<void> {
    return observableOf(null);
  }

  public getAll(): Observable<OverviewListMachineForDocumentation[]> {
    return this.httpClient.get(`${this.backendConfiguration.getMachineOverviewEndpoint()}machinedocumentation/machines`).pipe(
      rxjsMap((machineDocumentationsJSON: any) =>
        map(machineDocumentationsJSON, (machineDocumentationJSON: any) => {
          return OverviewListMachineForDocumentation.fromJSON(machineDocumentationJSON);
        })
      )
    );
  }

  public getById(id: number): Observable<OverviewListMachineForDocumentation> {
    return this.httpClient
      .get(`${this.backendConfiguration.getMachineOverviewEndpoint()}machinedocumentation/machines/${id}`)
      .pipe(rxjsMap((machineDocumentationsJSON: any) => OverviewListMachineForDocumentation.fromJSON(machineDocumentationsJSON)));
  }

  public save(machine: OverviewListMachineForDocumentation): Observable<number> {
    return observableOf(null);
  }

  public update(machine: OverviewListMachineForDocumentation): Observable<void> {
    return observableOf(null);
  }

  public getMachineGeneralDocumentations(machineId: number): Observable<MachineGeneralDocumentation[]> {
    return this.httpClient
      .get(`${this.backendConfiguration.getMachineOverviewEndpoint()}machinedocumentation/machine/${machineId}/general`)
      .pipe(
        rxjsMap((machineGeneralDocumentationsJSON: any) =>
          map(machineGeneralDocumentationsJSON, (machineGeneralDocumentationJSON: any) => MachineGeneralDocumentation.fromJSON(machineGeneralDocumentationJSON))
        )
      );
  }

  public getMachineTechnicalWeaveSettings(machineId: number): Observable<MachineTechnicalWeaveSettings[]> {
    return this.httpClient
      .get(`${this.backendConfiguration.getMachineOverviewEndpoint()}machinedocumentation/machine/${machineId}/technical-weave-settings`)
      .pipe(
        rxjsMap((machineTechnicalWeaveSettingsJSON: any) =>
          map(machineTechnicalWeaveSettingsJSON, (machineTechnicalWeaveSettingJSON: any) => MachineTechnicalWeaveSettings.fromJSON(machineTechnicalWeaveSettingJSON))
        )
      );
  }

  public getMachineSparePartsCatalogue(machineId: number): Observable<MachineSparePartsCatalogue> {
    return this.httpClient
      .get(`${this.backendConfiguration.getMachineOverviewEndpoint()}machinedocumentation/machine/${machineId}/spare-parts-catalog`)
      .pipe(rxjsMap((machineSparePartsCatalogueJSON: any) => MachineSparePartsCatalogue.fromJSON(machineSparePartsCatalogueJSON)));
  }

  public getMachineManuals(machineId: number): Observable<MachineManual[]> {
    return this.httpClient
      .get(`${this.backendConfiguration.getMachineOverviewEndpoint()}machinedocumentation/machine/${machineId}/manuals`)
      .pipe(rxjsMap((machineManualsJSON: any) => map(machineManualsJSON, (machineManualJSON: any) => MachineManual.fromJSON(machineManualJSON))));
  }

  public getMachineDitaManual(machineId: number, type: DitaManualType): Observable<string> {
    return this.httpClient
      .get(`${this.backendConfiguration.getMachineOverviewEndpoint()}machinedocumentationdita/${machineId}/index?type=${type}`)
      .pipe(rxjsMap((machineDitaManualUrl: string) => machineDitaManualUrl));
  }

  public getMachineSchematics(machineId: number): Observable<MachineSchematic[]> {
    return this.httpClient
      .get(`${this.backendConfiguration.getMachineOverviewEndpoint()}machinedocumentation/machine/${machineId}/schematics`)
      .pipe(rxjsMap((machineSchematicsJSON: any) => map(machineSchematicsJSON, (machineSchematicJSON: any) => MachineSchematic.fromJSON(machineSchematicJSON))));
  }

  public downloadMachineGeneralDocumentationFile(machineId: number, number: string): Observable<HttpResponse<Blob>> {
    return this.httpClient.post(
      `${this.backendConfiguration.getMachineOverviewEndpoint()}machinedocumentation/machine/${machineId}/general/file`,
      {number},
      {
        observe: 'response',
        responseType: 'blob'
      }
    );
  }

  public downloadMachineTechnicalWeaveSettingsFile(
    machineId: number,
    technicalWeaveSettingsType: string,
    technicalWeaveSettingsRevision: MachineTechnicalWeaveSettingsRevision
  ): Observable<HttpResponse<Blob>> {
    const technicalWeaveSettingsRevisionJSON = technicalWeaveSettingsRevision.toJSON();
    const body = {type: technicalWeaveSettingsType, ...technicalWeaveSettingsRevisionJSON};

    return this.httpClient.post(`${this.backendConfiguration.getMachineOverviewEndpoint()}machinedocumentation/machine/${machineId}/technical-weave-settings/file`, body, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  public downloadMachineSetupFile(machineId: number): Observable<HttpResponse<Blob>> {
    return this.httpClient.get(`${this.backendConfiguration.getMachineOverviewEndpoint()}machinedocumentation/machine/${machineId}/software/file`, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  public isSoftwareAvailable(machineId: number): Observable<boolean> {
    return this.httpClient
      .get(`${this.backendConfiguration.getMachineOverviewEndpoint()}machinedocumentation/machine/${machineId}/software/available`)
      .pipe(rxjsMap((isSoftwareAvailable: boolean) => isSoftwareAvailable));
  }

  public downloadMachineSparePartsCatalogueFile(machineId: number, name: string): Observable<HttpResponse<Blob>> {
    return this.httpClient.post(
      `${this.backendConfiguration.getMachineOverviewEndpoint()}machinedocumentation/machine/${machineId}/spare-parts-catalog/file`,
      {name},
      {
        observe: 'response',
        responseType: 'blob'
      }
    );
  }

  public downloadMachineManualFile(machineId: number, machineManual: MachineManual): Observable<HttpResponse<Blob>> {
    return this.httpClient.post(`${this.backendConfiguration.getMachineOverviewEndpoint()}machinedocumentation/machine/${machineId}/manuals/file`, machineManual, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  public downloadMachineSchematicFile(machineId: number, name: string): Observable<HttpResponse<Blob>> {
    return this.httpClient.post(
      `${this.backendConfiguration.getMachineOverviewEndpoint()}machinedocumentation/machine/${machineId}/schematics/file`,
      {name},
      {
        observe: 'response',
        responseType: 'blob'
      }
    );
  }

  public renameMachine(machineId: number, machineName: string): Observable<void> {
    return this.httpClient.put<void>(`${this.backendConfiguration.getMachineEndpoint()}machines/machine/rename/${machineId}`, {name: machineName});
  }
}
